import React, { useState, useEffect, useRef } from "react";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import axiosInstance from "../axiosConfig";

export default function ExpenseForm() {

  const fileRef = useRef();

  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  const toggleSidebar = () => setIsSidebarVisible(!isSidebarVisible);

  const [ledgerList, setLedgerList] = useState([]);
  const [budgetList, setBudgetList] = useState([]);
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    ledger_name: "",
    ledger_code: "",
    date: "",
    amount: "",
    amount_in_words: "",
    budget_head: "",
    voucher_no: "",
    employee_email: "",
    employee_name: "",
    details: ""
  });

  const numberToWords = (num) => {
    if (!num) return "";

    const a = [
      "", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine",
      "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen",
      "Sixteen", "Seventeen", "Eighteen", "Nineteen"
    ];

    const b = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];

    const inWords = (n) => {
      if (n < 20) return a[n];
      if (n < 100) return b[Math.floor(n / 10)] + " " + a[n % 10];
      if (n < 1000) return a[Math.floor(n / 100)] + " Hundred " + inWords(n % 100);
      if (n < 100000) return inWords(Math.floor(n / 1000)) + " Thousand " + inWords(n % 1000);
      if (n < 10000000) return inWords(Math.floor(n / 100000)) + " Lakh " + inWords(n % 100000);
      return inWords(Math.floor(n / 10000000)) + " Crore " + inWords(n % 10000000);
    };

    return inWords(parseInt(num)) + " Only";
  };

  const [errors, setErrors] = useState({});
  const [file, setFile] = useState(null);

  useEffect(() => {
    loadBudget();
  }, []);

  /* ================= FETCH ================= */
  const searchLedger = async (text) => {
    if (!text) return setLedgerList([]);

    const res = await axiosInstance.get(`/api/ledger?search=${text}`);
    setLedgerList(res.data);
  };

  const loadBudget = async () => {
    const res = await axiosInstance.get("/api/budget-head");
    setBudgetList(res.data);
  };

  /* ================= VALIDATION ================= */
  const validateForm = () => {
    let newErrors = {};

    if (!form.ledger_name) newErrors.ledger_name = "Ledger is required";
    if (!form.ledger_code) newErrors.ledger_code = "Ledger code required";
    if (!form.date) newErrors.date = "Date required";
    if (!form.amount || form.amount <= 0) newErrors.amount = "Valid amount required";
    if (!form.amount_in_words) newErrors.amount_in_words = "Amount in Words required";
    if (!form.budget_head) newErrors.budget_head = "Budget required";
    if (!form.employee_email) newErrors.employee_email = "Email required";
    if (!form.employee_name) newErrors.employee_name = "Name required";

    //if (!form.voucher_no) newErrors.voucher_no = "Voucher No required";
    if (!form.details) newErrors.details = "Details required";
    if (!file) newErrors.file = "Bill upload required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /* ================= SUBMIT ================= */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      setLoading(true);

      const data = new FormData();
      Object.keys(form).forEach(key => data.append(key, form[key]));
      if (file) data.append("file", file);

      await axiosInstance.post("/api/expense", data);

      // RESET
      setForm({
        ledger_name: "",
        ledger_code: "",
        date: "",
        amount: "",
        amount_in_words: "",
        budget_head: "",
        voucher_no: "",
        employee_email: "",
        employee_name: "",
        details: ""
      });

      setErrors({});
      setFile(null);
      setLedgerList([]);

      if (fileRef.current) fileRef.current.value = "";

    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="d-flex dashboard">

      {/* SIDEBAR */}
      <div className={isSidebarVisible ? "sidebar-container" : "sidebar-hidden"}>
        <Sidebar />
      </div>

      <div className="flex-grow-1">

        {/* HEADER */}
        <Header toggleSidebar={toggleSidebar} isSidebarVisible={isSidebarVisible} />

        <main className="main-container p-4">

          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-4">
            <h3 className="mb-0">Expense Entry</h3>
            <a className="btn btn-outline-primary" href="/ai-form-assistant?form=expense">AI se form bharein · Chat / Voice</a>
          </div>

          <div className="card shadow border-0">
            <div className="card-header bg-dark text-white">
              Expense Entry Form
            </div>

            <div className="card-body">

              <form onSubmit={handleSubmit}>
                <div className="row">

                  {/* Ledger */}
                  <div className="col-md-6 mb-3 position-relative">
                    <label>Ledger Name</label>
                    <input
                      className={`form-control ${errors.ledger_name && "is-invalid"}`}
                      value={form.ledger_name}
                      onChange={(e) => {
                        setForm({ ...form, ledger_name: e.target.value });
                        searchLedger(e.target.value);
                      }}
                    />
                    {errors.ledger_name && <div className="text-danger small">{errors.ledger_name}</div>}

                    {ledgerList.length > 0 && (
                      <div className="list-group position-absolute w-100 shadow">
                        {ledgerList.map((l, i) => (
                          <button
                            key={i}
                            type="button"
                            className="list-group-item list-group-item-action"
                            onClick={() => {
                              setForm({
                                ...form,
                                ledger_name: l.CleanLogicName,
                                ledger_code: l.LogicCode
                              });
                              setLedgerList([]);
                            }}
                          >
                            {l.CleanLogicName}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Ledger Code */}
                  <div className="col-md-6 mb-3">
                    <label>Ledger Code</label>
                    <input
                      className={`form-control bg-light ${errors.ledger_code && "is-invalid"}`}
                      value={form.ledger_code}
                      readOnly
                    />
                    {errors.ledger_code && <div className="text-danger small">{errors.ledger_code}</div>}
                  </div>

                  {/* Date */}
                  <div className="col-md-4 mb-3">
                    <label>Date</label>
                    <input
                      type="date"
                      className={`form-control ${errors.date && "is-invalid"}`}
                      value={form.date}
                      onChange={(e) => setForm({ ...form, date: e.target.value })}
                    />
                    {errors.date && <div className="text-danger small">{errors.date}</div>}
                  </div>

                  {/* Amount */}
                  <div className="col-md-4 mb-3">
                    <label>Amount</label>
                    <input
                      type="number"
                      className={`form-control ${errors.amount && "is-invalid"}`}
                      value={form.amount}
                      onChange={(e) => {
                        const value = e.target.value;

                        setForm({
                          ...form,
                          amount: value,
                          amount_in_words: numberToWords(value)
                        });
                      }}
                    />
                    {errors.amount && <div className="text-danger small">{errors.amount}</div>}
                  </div>

                  {/* Amount in Words */}
                  <div className="col-md-4 mb-3">
                    <label>Amount In Words</label>
                    <input
                      type="text"
                      className={`form-control ${errors.amount_in_words && "is-invalid"}`}
                      value={form.amount_in_words}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          amount_in_words: e.target.value
                        })
                      }
                    />
                    {errors.amount_in_words && <div className="text-danger small">{errors.amount_in_words}</div>}
                  </div>

                  {/* Budget */}
                  <div className="col-md-4 mb-3">
                    <label>Budget Head</label>
                    <select
                      className={`form-select ${errors.budget_head && "is-invalid"}`}
                      value={form.budget_head}
                      onChange={(e) => setForm({ ...form, budget_head: e.target.value })}
                    >
                      <option value="">Select Budget</option>
                      {budgetList.map((b, i) => (
                        <option key={i}>{b}</option>
                      ))}
                    </select>
                    {errors.budget_head && <div className="text-danger small">{errors.budget_head}</div>}
                  </div>

                  {/* Email */}
                  <div className="col-md-4 mb-3">
                    <label>Employee Email</label>
                    <input
                      className={`form-control ${errors.employee_email && "is-invalid"}`}
                      value={form.employee_email}
                      onChange={(e) => setForm({ ...form, employee_email: e.target.value })}
                    />
                    {errors.employee_email && <div className="text-danger small">{errors.employee_email}</div>}
                  </div>

                  {/* Name */}
                  <div className="col-md-4 mb-3">
                    <label>Employee Name</label>
                    <input
                      className={`form-control ${errors.employee_name && "is-invalid"}`}
                      value={form.employee_name}
                      onChange={(e) => setForm({ ...form, employee_name: e.target.value })}
                    />
                    {errors.employee_name && <div className="text-danger small">{errors.employee_name}</div>}
                  </div>

                  {/* Voucher */}
                  {/*<div className="col-md-6 mb-3">
                    <label>Voucher No</label>
                    <input
                      className="form-control"
                      value={form.voucher_no}
                      onChange={(e) => setForm({ ...form, voucher_no: e.target.value })}
                    />
                    {errors.voucher_no && (
                      <div className="text-danger small">{errors.voucher_no}</div>
                    )}
                  </div>*/}

                  {/* Details */}
                  <div className="col-md-12 mb-3">
                    <label>Details</label>
                    <textarea
                      className="form-control"
                      value={form.details}
                      onChange={(e) => setForm({ ...form, details: e.target.value })}
                    />
                    {errors.details && (
                      <div className="text-danger small">{errors.details}</div>
                    )}

                  </div>

                  {/* File */}
                  <div className="col-md-12 mb-3">
                    <label>Upload Bill</label>
                    <input
                      ref={fileRef}
                      type="file"
                      className="form-control"
                      onChange={(e) => setFile(e.target.files[0])}
                    />

                    {errors.file && (
                      <div className="text-danger small">{errors.file}</div>
                    )}

                    {file && (
                      <div className="text-success small mt-1">
                        {file.name}
                      </div>
                    )}

                  </div>

                </div>

                <div className="text-end">
                  <button disabled={loading} className="btn btn-success">
                    {loading ? "Saving..." : "Save Expense"}
                  </button>
                </div>

              </form>

            </div>
          </div>

        </main>
      </div>
    </div>
  );
}
